import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import FilesInput from '../common/components/formInputs/FilesInput'
import TextInput from '../common/components/formInputs/TextInput'
import SelectInput from '../common/components/formInputs/SelectInput'
import TextareaInput from '../common/components/formInputs/TextareaInput'
import ImageInput from '../common/components/formInputs/ImageInput'
import { AVAILABLE_FILE_EXTENSIONS, MAX_FILES_NUMBER } from '../common/helpers'

const FirstStep = ({
  displayStep,
  isPropertyManagerOrFrontDesk,
  buildingOptions,
  eventTypeOptions,
  trainerOptions,
  formValues,
  setFieldValue,
}) => {
  const { t } = useTranslation(['eventManagement'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null

  useEffect(() => {
    if (formValues.building) {
      return
    }

    const defaultBuilding = buildingOptions.find((option) => option.id === selectedBuildingId)

    if (!defaultBuilding) {
      return
    }

    setFieldValue('building', defaultBuilding)
  }, [selectedBuildingId, formValues, buildingOptions])

  if (!displayStep) {
    return null
  }

  return (
    <React.Fragment>
      <div className="columns">
        <SelectInput
          columnSize={isPropertyManagerOrFrontDesk ? 12 : 6}
          name="building"
          label={t('eventManagement:eventFormPage.firstStep.buildingLabel')}
          options={buildingOptions}
          value={formValues.building}
          setFieldValue={setFieldValue}
        />
        {!isPropertyManagerOrFrontDesk && (
          <SelectInput
            columnSize={6}
            name="type"
            label={t('eventManagement:eventFormPage.firstStep.typeLabel')}
            options={eventTypeOptions}
            value={formValues.type}
            setFieldValue={setFieldValue}
          />
        )}
      </div>
      <div className="columns">
        <TextInput
          columnSize={isPropertyManagerOrFrontDesk ? 12 : 6}
          name="name"
          label={t('eventManagement:eventFormPage.firstStep.nameLabel')}
          placeholder={t('eventManagement:eventFormPage.firstStep.namePlaceholder')}
          value={formValues.name}
          setFieldValue={setFieldValue}
        />
        {!isPropertyManagerOrFrontDesk && (
          <SelectInput
            columnSize={6}
            name="organizer"
            label={t('eventManagement:eventFormPage.firstStep.facilitatorLabel')}
            options={trainerOptions}
            value={formValues.organizer}
            setFieldValue={setFieldValue}
          />
        )}
      </div>
      <div className="columns">
        <TextareaInput
          columnSize={12}
          name="description"
          label={t('eventManagement:eventFormPage.firstStep.descriptionLabel')}
          placeholder={t('eventManagement:common.overviewPlaceholder')}
          value={formValues.description}
          setFieldValue={setFieldValue}
        />
      </div>
      <div className="columns">
        <ImageInput
          columnSize={12}
          name="image"
          value={formValues.image}
          eventName={formValues.name}
          eventType="Event"
          setFieldValue={setFieldValue}
        />
      </div>
      <div className="columns">
        <FilesInput
          columnsSize={12}
          label={t('eventManagement:eventFormPage.firstStep.additionalAssetsLabel')}
          files={formValues.files}
          handleFieldChange={(e) => setFieldValue('files', e.target.value)}
          accept={AVAILABLE_FILE_EXTENSIONS}
          maxNumberOfFiles={MAX_FILES_NUMBER}
          maxFileSizeInMB={3}
        />
      </div>
    </React.Fragment>
  )
}

FirstStep.propTypes = {
  displayStep: PropTypes.bool,
  buildingOptions: PropTypes.array,
  eventTypeOptions: PropTypes.array,
  trainerOptions: PropTypes.array,
  formValues: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
}

export default FirstStep
